import React, { useState, useEffect } from "react";
import { styled } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Autocomplete, Button, Grid, Paper, Typography, Select, MenuItem, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import TableN from "../../reusableComponents/components/generics/TableN";
import { fetchInsureeClaimService } from "../../action";
import { decodeId } from "../../utils/helper";
import Delete from "@mui/icons-material/Delete";
import _ from "lodash";

const StyledPaper = styled(Paper)(({ theme }) => ({
  ...theme.paper.paper,
}));

const StyledPaperHeader = styled(Grid)(({ theme }) => ({
  ...theme.paper.header,
  fontSize: 600,
  padding: ".8rem",
}));

function ClaimActsPannel({ onDataChange, claimData, disabled }) {
  const SignInSuccess = useSelector((state) => state);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [totalClaimed, setTotalClaimed] = useState(0);


  const InsureeCode = SignInSuccess?.profileReducer?.insureeByIdResponseData;
  const priceListId = SignInSuccess?.fosaReducer?.pickerHealthFacilityInsurees;
  const serviceId = priceListId?.[0]?.servicesPricelist?.id;
  const hfId = priceListId?.[0]?.id;
  const insureeId = InsureeCode?.[0]?.id;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const [deleteType, setDeleteType] = useState(false);
  const [index, setIndex] = useState("");

  // New state to store the previous value before change
  const [prevRows, setPrevRows] = useState([]);

  useEffect(() => {
    if (serviceId && hfId && insureeId) {
      dispatch(fetchInsureeClaimService(serviceId, hfId, insureeId));
    }
  }, [dispatch, serviceId, hfId, insureeId]);

  useEffect(() => {
    if (SignInSuccess?.fosaReducer?.ClaimServices) {
      const services = SignInSuccess.fosaReducer.ClaimServices;
      const filtered = services.filter(
        (service) => service.categoryType === "A"
      );
      setFilteredServices(filtered);
    }
  }, [SignInSuccess]);

  useEffect(() => {
    if (claimData?.services && claimData.services.length > 0) {
      const initialRows = claimData.services
        .filter((service) => service.service.categoryType === "A")
        .map((service) => {
          const jsonExt = JSON.parse(service.jsonExt || "{}");
          return {
            serviceId: decodeId(service.service?.id),
            service: service.service.name,
            price: service.service.price,
            quantity: service.qtyProvided,
            priceAsked: service.priceAsked,
            countedPrice: jsonExt.countedPrice,
            tmValue: jsonExt.tmValue,
            explanation: service.explanation,
          };
        });
      setRows(initialRows);
      setPrevRows(initialRows);  // Save the initial state as the previous state
      setSelectedServices(initialRows.map((row) => row.service));
      calculateTotalClaimed(initialRows);
      onDataChange(initialRows);
    } else {
      setRows([
        {
          service: "",
          quantity: "",
          price: "",
          tmValue: "",
          countedPrice: "",
          explanation: "",
          serviceId: "",
          priceAsked: "",
        },
      ]);
    }
  }, [claimData]);

  const handleServiceChange = (index, event) => {
    let data = SignInSuccess?.fosaReducer?.ClaimServices;
    const requiredType = data[index].requiredType;
    const serviceName = event.target.value;

    // Store the current state as previous before change
    setPrevRows(rows);


    if (requiredType === 2) {     
      setIsPopupOpen(true);
      setPopupMessage('Preauthorization is required');
      setDeleteType('delete');
      setIndex(index);
      return;  // Exit early to prevent state change
    }

    const selectedService = filteredServices.find(service => service.name === serviceName);
    const jsonExt = JSON.parse(selectedService?.jsonExt || '{}');
    const newRows = rows.map((row, i) =>
      i === index ? {
        ...row,
        serviceId: selectedService?.id,
        service: serviceName,
        price: selectedService?.price,
        quantity: 1,
        priceAsked: selectedService?.price,
        countedPrice: jsonExt.countedPrice,
        tmValue: jsonExt.tmValue,
      } : row
    );
    setRows(newRows);
    onDataChange(newRows);
    const newSelectedServices = newRows
      .map((row) => row.service)
      .filter((service) => service !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
  };

  const handleChange = (index, event) => {
    let data = SignInSuccess?.fosaReducer?.ClaimServices;

    const fullAmount = data[index].requiredFullAmt;
    const requiredQty = data[index].requiredQty;
    const requiredType = data[index].requiredType;
    const priceAsked = data[index].price || 0;
    const qtyProvided = data[index].qtyProvided || 0;
    const jsonExtObject = JSON.parse(data[index]?.jsonExt);
    const totalClaimed = jsonExtObject.countedPrice * qtyProvided;

    setPrevRows(rows);

    if (requiredType === 3) {
      if (!!fullAmount && totalClaimed > fullAmount) {
        setIsPopupOpen(true);
        setPopupMessage('Selected price is more than prior authorized full amount');
        setIndex(index);
        return;  // Exit early to prevent state change
      }
      if (!!requiredQty && qtyProvided > requiredQty) {
        setIsPopupOpen(true);
        setPopupMessage('Selected price is more than prior authorized full amount');
        setIndex(index);
        return;  // Exit early to prevent state change
      }
    }

    const { name, value } = event.target;
    const newRows = rows.map((row, i) =>
      i === index ? { ...row, [name]: value } : row
    );
    setRows(newRows);
    onDataChange(newRows);
    if (name === 'quantity') {
      calculateTotalClaimed(newRows);
    }
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        service: "",
        quantity: "",
        price: "",
        tmValue: "",
        countedPrice: "",
        explanation: "",
        serviceId: "",
        priceAsked: "",
      },
    ]);
  };

  const deleteRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    const newSelectedServices = newRows
      .map((row) => row.service)
      .filter((service) => service !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
    onDataChange(newRows);
  };

  const getAvailableServices = (currentService) => {
    return filteredServices.filter(
      (service) =>
        !selectedServices.includes(service.name) ||
        service.name === currentService
    );
  };

  const headers = () => {
    return [
      t("Service"),
      t("Quantity"),
      t("Price"),
      t("TicketModerateur"),
      t("CountedPrice"),
      t("Explanation"),
      t("Action"),
    ];
  };

  const calculateTotalClaimed = (rows) => {
    const total = rows.reduce((sum, row) => sum + row.quantity * row.price, 0);
    setTotalClaimed(total);
  };

  const totalTmValue = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const tmValue = r.tmValue ? r.tmValue : null;
      return sum + (tmValue ? parseFloat(tmValue) * quantity : 0);
    }, 0),
    2
  );

  const totalCounterPriceValue = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const countedPrice = r.countedPrice ? r.countedPrice : null;
      return sum + (countedPrice ? parseFloat(countedPrice) * quantity : 0);
    }, 0),
    2
  );

  const multiplyVal = (value, multiplyBy) => {
    return parseFloat(value) * parseFloat(multiplyBy);
  };

  const itemFormatters = () => {
    return [
      (row, index) => (
        <div>
          <Autocomplete
            options={getAvailableServices(row.service).map((service) => ({
              label: service.code,
              id: service.name
            }))}
            sx={{ width: 200 }}
            size="small"
            value={row.service ? getAvailableServices(row.service).find(service => service.name === row.service)?.code : null}
            disabled={disabled}
            onChange={(e, option) => {
              console.log("===> option", option)
              handleServiceChange(index, { target: { value: option?.id } });
            }}
            renderInput={(params) => <TextField {...params} />}
          />
          {/* <Select
            value={row.service}
            onChange={(e) => handleServiceChange(index, e)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">
              <em>{t("Select")}</em>
            </MenuItem>
            {getAvailableServices(row.service).map((service) => (
              <MenuItem key={service?.id} value={service.name}>
                {service.code}
              </MenuItem>
            ))}
          </Select> */}
        </div>
      ),
      (row, index) => (
        <TextField
          type="number"
          className="no-arrow"
          name="quantity"
          size="small"
          value={row.quantity ? row.quantity : ""}
          onChange={(e) => handleChange(index, e)}
          disabled={disabled}
        />
      ),
      (row) => (
        <TextField
          type="text"
          name="price"
          value={row.price ? row.price : ""}
          size="small"
          disabled={true}
          onChange={(e) => handleChange(row, e, "price")}
        />
      ),
      (row) => (
        <TextField
          type="text"
          name="ticket"
          size="small"
          value={row.tmValue ? multiplyVal(row.tmValue, row.quantity) : ""}
          disabled={true}
          onChange={(e) => handleChange(row, e, "ticket")}
        />
      ),
      (row) => (
        <TextField
          type="text"
          name="countedPrice"
          size="small"
          value={row.tmValue ? multiplyVal(row.countedPrice, row.quantity) : ""}
          disabled={true}
          onChange={(e) => handleChange(row, e, "countedPrice")}
        />
      ),
      (row) => (
        <TextField
          type="text"
          name="explanation"
          size="small"
          disabled={true}
          value={row.explanation ? row.explanation : ""}
          onChange={(e) => handleChange(row, e, "explanation")}
        />
      ),
      (row, index) => (
        disabled ? (<div></div>) : <Button startIcon={<Delete style={{ color: "red" }} />} onClick={() => deleteRow(index)}></Button>
      )
    ];
  };

  const preHeaders = [
    "\u200b",
    "",
    totalClaimed > 0 ? t("Total Claimed: ") + totalClaimed : "",
    totalTmValue > 0 ? t("TotalTm") + totalTmValue : "",
    totalCounterPriceValue > 0
      ? t("TotalCounted") + totalCounterPriceValue
      : "",
    " ",
    " ",
  ];

  const handleClose = () => {
    setIsPopupOpen(false);
    if (deleteType === 'delete') {
      deleteRow(index);
    } else {
      // Revert to previous rows if popup is triggered and deleteType is not 'delete'
      setRows(prevRows);
      onDataChange(prevRows);
      calculateTotalClaimed(prevRows);
    }
  };

  return (
    <StyledPaper>
      <StyledPaperHeader container alignItems="center" direction="row">
        <Grid item alignItems="center">
          <Typography variant="h6">{t("ClaimActsServices")}</Typography>
        </Grid>
      </StyledPaperHeader>
      <TableN
        headers={headers()}
        itemFormatters={itemFormatters()}
        preHeaders={preHeaders}
        items={rows}
        withSelection={"single"}
        withPagination={false}
      />
      {!disabled && <Button onClick={addRow}>{t("Add")}</Button>}

      <Dialog open={isPopupOpen} onClose={handleClose}>
        <DialogTitle>{t("Alert")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(popupMessage)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("OK")}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledPaper>
  );
}

export default ClaimActsPannel;
